import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Menu, MenuItem } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useSelector } from "react-redux";
import { primaryColor } from "../../Theme";
import ChatbotIcon from "../ChatbotIcon";
import RxExperiencePreferenceDialog from "../AIPrescription/RxExperiencePreferenceDialog";
import {
  AGENTIC_RX_PATH,
  NEW_RX_PATH,
  RX_EXPERIENCE_PROMPT_STATE_KEY,
  RxExperiencePath,
  RxExperiencePrompt,
  getDefaultRxExperience,
  isRxExperienceHome,
  markRxExperienceUsed,
  promptForRxSwitch,
  rxExperienceFromPathname,
  setDefaultRxExperience,
} from "../../utils/AIPrescription/rxExperiencePreference";

export { AGENTIC_RX_PATH, NEW_RX_PATH };

/**
 * Pad mode switcher shown as the first item of the tab bar. Split control:
 *  - clicking the LABEL opens that pad's screen (e.g. from Saved Prescriptions,
 *    clicking "AgenticRx" returns to the Agentic pad),
 *  - clicking the ARROW opens the options (AgenticRx / New Rx Pad) to switch.
 *
 * The chip reflects the pad you're currently in and persists across the shared
 * section tabs: AgenticRx while in the Agentic flow, New Rx Pad while in that
 * flow. When leaving the Agentic pad the caller can run `onBeforeNavigate`
 * first (auto-save drafts); navigation waits for that promise before routing.
 */

// Remembers the pad the user is in so the chip label stays correct on the shared
// section pages (Allergies, Saved Prescriptions, …). New Rx Pad is the default.
const CURRENT_PAD_KEY = "ai.currentPad";

interface PadOption {
  value: RxExperiencePath;
  label: string;
  hint: string;
}

const PAD_OPTIONS: PadOption[] = [
  {
    value: NEW_RX_PATH,
    label: "New Rx Pad",
    hint: "Classic prescribing pad",
  },
  {
    value: AGENTIC_RX_PATH,
    label: "AgenticRx",
    hint: "Voice and AI prescribing",
  },
];

interface PadSwitcherProps {
  /**
   * Optional hook run before routing to another pad. Return a promise to defer
   * navigation until it settles (used to flush the auto-save on switch).
   */
  onBeforeNavigate?: (from: string, to: string) => void | Promise<void>;
}

function activePathFrom(pathname: string, servicesData?: any): RxExperiencePath {
  const fromPath = rxExperienceFromPathname(pathname);
  if (fromPath) return fromPath;
  try {
    const saved = sessionStorage.getItem(CURRENT_PAD_KEY);
    if (saved === NEW_RX_PATH || saved === AGENTIC_RX_PATH) return saved;
  } catch {
    /* sessionStorage unavailable — fall through */
  }
  return getDefaultRxExperience(servicesData);
}

function readPromptFromState(state: unknown): RxExperiencePrompt | undefined {
  if (!state || typeof state !== "object") return undefined;
  const value = (state as Record<string, unknown>)[RX_EXPERIENCE_PROMPT_STATE_KEY];
  if (value === "choose" || value === "confirm") return value;
  return undefined;
}

function stripPromptFromState(state: unknown): unknown {
  if (!state || typeof state !== "object") return state;
  const { [RX_EXPERIENCE_PROMPT_STATE_KEY]: _prompt, ...rest } = state as Record<
    string,
    unknown
  >;
  return Object.keys(rest).length > 0 ? rest : undefined;
}

const PadSwitcher: React.FC<PadSwitcherProps> = ({ onBeforeNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const servicesData = useSelector((s: any) => s?.UserServicesSlice?.ServicesData);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);
  const [switching, setSwitching] = useState(false);
  const [prompt, setPrompt] = useState<RxExperiencePrompt | null>(null);
  const consumedPromptKey = useRef<string | null>(null);
  const open = Boolean(anchorEl);

  const padOnThisPage = rxExperienceFromPathname(location.pathname);

  // Persist the active pad whenever we're actually on one, so the shared section
  // pages show the correct chip label. Also record that this experience was used
  // and surface the default-preference prompt when needed.
  useEffect(() => {
    try {
      if (padOnThisPage) {
        sessionStorage.setItem(CURRENT_PAD_KEY, padOnThisPage);
        markRxExperienceUsed(padOnThisPage);
      }
    } catch {
      /* ignore */
    }

    const onPadHome = isRxExperienceHome(location.pathname);
    const navPrompt = readPromptFromState(location.state);
    const promptKey = `${location.pathname}:${navPrompt ?? ""}`;
    if (onPadHome && navPrompt && consumedPromptKey.current !== promptKey) {
      consumedPromptKey.current = promptKey;
      setPrompt(navPrompt);
      navigate(location.pathname, {
        replace: true,
        state: stripPromptFromState(location.state),
      });
      return;
    }
  }, [location.pathname, location.state, navigate, padOnThisPage]);

  const current = activePathFrom(location.pathname, servicesData);
  const defaultPath = getDefaultRxExperience(servicesData);
  const currentLabel =
    PAD_OPTIONS.find((o) => o.value === current)?.label ?? "New Rx Pad";

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(chipRef.current ?? e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // Navigate to a pad. Skips only when we're already ON that pad's page (so the
  // label still works from a shared section page). Runs onBeforeNavigate first.
  const goToPad = async (to: RxExperiencePath) => {
    handleClose();
    if (location.pathname.startsWith(to) || switching) return;
    try {
      setSwitching(true);
      if (onBeforeNavigate) {
        await onBeforeNavigate(current, to);
      }
      const switchPrompt = promptForRxSwitch(current, to, servicesData);
      const prevState = location.state;
      const nextState =
        prevState && typeof prevState === "object" && !Array.isArray(prevState)
          ? { ...(prevState as Record<string, unknown>) }
          : {};
      if (switchPrompt) {
        nextState[RX_EXPERIENCE_PROMPT_STATE_KEY] = switchPrompt;
      }
      navigate(to, {
        state: Object.keys(nextState).length > 0 ? nextState : undefined,
      });
    } finally {
      setSwitching(false);
    }
  };

  const closePrompt = () => setPrompt(null);

  const handleChooseDefault = (path: RxExperiencePath) => {
    setDefaultRxExperience(path);
    closePrompt();
  };

  const handleConfirmNewDefault = () => {
    if (padOnThisPage) setDefaultRxExperience(padOnThisPage);
    closePrompt();
  };

  // Highlight (teal fill) only when this pad's page is actually open — matching
  // the other tabs, which are plain until selected. On the shared section pages
  // the chip is inactive (white bg / teal text) like an unselected tab.
  const isActive = location.pathname.startsWith(current);
  const fg = isActive ? "#ffffff" : primaryColor;
  const bg = isActive ? primaryColor : "transparent";

  const btnBase: React.CSSProperties = {
    backgroundColor: "transparent",
    color: fg,
    border: "none",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "inherit",
    lineHeight: 1.2,
    cursor: switching ? "default" : "pointer",
    display: "inline-flex",
    alignItems: "center",
  };

  return (
    <Box sx={{ display: "inline-flex" }}>
      {/* Split control with inline styles so the tab-bar CSS can't blank it. */}
      <div
        ref={chipRef}
        style={{
          display: "inline-flex",
          alignItems: "stretch",
          borderRadius: 8,
          overflow: "hidden",
          backgroundColor: bg,
        }}
      >
        {/* Label — opens the current pad's screen */}
        <button
          type="button"
          onClick={() => goToPad(current)}
          disabled={switching}
          aria-label={`Open ${currentLabel}`}
          style={{ ...btnBase, padding: "7px 12px", whiteSpace: "nowrap" }}
        >
          {currentLabel}
        </button>
        {/* Arrow — opens the pad options */}
        <button
          id="pad-switcher-button"
          type="button"
          aria-controls={open ? "pad-switcher-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleOpen}
          disabled={switching}
          aria-label="Switch Rx experience"
          style={{
            ...btnBase,
            padding: "7px 8px 7px 2px",
          }}
        >
          <KeyboardArrowDownIcon
            style={{
              fontSize: 18,
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 0.15s ease",
            }}
          />
        </button>
      </div>
      <Menu
        id="pad-switcher-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{
          dense: true,
          "aria-labelledby": "pad-switcher-button",
          sx: { py: 0 },
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.75,
            minWidth: 260,
            maxWidth: "calc(100vw - 24px)",
            borderRadius: "14px",
            py: 0.75,
            px: 0.75,
            boxShadow: `0 8px 28px ${primaryColor}22`,
            border: `1px solid ${primaryColor}22`,
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            px: 1,
            pt: 0.35,
            pb: 0.75,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          Switch Rx experience
        </Box>
        {PAD_OPTIONS.map((opt) => {
          const selected = opt.value === current;
          const isDefault = opt.value === defaultPath;
          return (
            <MenuItem
              key={opt.value}
              selected={selected}
              disabled={switching}
              onClick={() => goToPad(opt.value)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                mx: 0,
                my: 0.25,
                px: 1,
                py: 1,
                borderRadius: "10px",
                minHeight: { xs: 48, sm: 44 },
                fontSize: 14,
                fontWeight: selected ? 700 : 600,
                color: selected ? primaryColor : "text.primary",
                whiteSpace: "normal",
                "&.Mui-selected": {
                  backgroundColor: `${primaryColor}14`,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: `${primaryColor}22`,
                },
                "&:hover": {
                  backgroundColor: selected ? `${primaryColor}22` : `${primaryColor}0a`,
                },
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  flex: "none",
                  backgroundColor: selected ? `${primaryColor}22` : `${primaryColor}12`,
                  color: primaryColor,
                }}
              >
                {opt.value === AGENTIC_RX_PATH ? (
                  <ChatbotIcon color={primaryColor} style={{ width: 18, height: 18 }} />
                ) : (
                  <EditNoteOutlinedIcon sx={{ fontSize: 18 }} />
                )}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    lineHeight: 1.2,
                  }}
                >
                  {opt.label}
                  {isDefault && (
                    <Box
                      component="span"
                      sx={{
                        px: 0.7,
                        py: 0.15,
                        borderRadius: "999px",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.02em",
                        textTransform: "uppercase",
                        backgroundColor: selected ? "#fff" : `${primaryColor}14`,
                        color: primaryColor,
                      }}
                    >
                      Default
                    </Box>
                  )}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: selected ? `${primaryColor}bb` : "text.secondary",
                    lineHeight: 1.3,
                    mt: 0.2,
                  }}
                >
                  {opt.hint}
                </Box>
              </Box>
              {selected ? (
                <CheckRoundedIcon sx={{ fontSize: 18, color: primaryColor, flex: "none" }} />
              ) : (
                <Box sx={{ width: 18, flex: "none" }} />
              )}
            </MenuItem>
          );
        })}
      </Menu>
      <RxExperiencePreferenceDialog
        open={prompt !== null}
        mode={prompt}
        currentPath={padOnThisPage}
        existingDefault={defaultPath}
        onChoose={handleChooseDefault}
        onConfirmChange={handleConfirmNewDefault}
        onKeepExisting={closePrompt}
      />
    </Box>
  );
};

export default PadSwitcher;
